import React from 'react'
import { usePageView, trackEvent } from '@fs/zion-analytics'
import {
  H1,
  ChoiceChips,
  ExpandableListItem,
  useOverlay,
  LayoutBand,
  List,
  ListItem,
  useStatusOverlay,
  Separator,
} from '@fs/zion-ui'

import { useTranslation } from 'react-i18next'
import { ArrowCaret, PlaceWorld } from '@fs/zion-icon'
import { appPath } from '@fs/zion-config'
import { useUsabilla } from '@fs/zion-usabilla'
import LangPicker from '@fs/zion-lang-picker'
import { i18n } from '@fs/zion-locale'
import { useDarkModeContext } from '../../providers/DarkModeProvider'
import useExternalLinks from '../../hooks/useExternalLinks'
import { usePWAMode } from '../../hooks'
import getIsTWAMode from '../../helpers/appMode_helper'
import findLang from '../../helpers/findLang'
import clearDiscoveryCodeNotification from '../../helpers/clearDiscoveryCodeNotification'

const MoreMenuItem = ({ icon, primaryText, to, external, onClick, target = '_self' }) => (
  <ListItem
    startElement={icon ? <ListItem.Icon Icon={icon} /> : null}
    primaryText={primaryText}
    to={to}
    target={target}
    external={external}
    onClick={onClick}
    endElement={<ListItem.Icon Icon={ArrowCaret} />}
  />
)

const ThemeMenuItem = () => {
  const { t } = useTranslation()
  const { handlePreferenceToggle } = useDarkModeContext()
  const chips = [
    { label: t('menu.theme.light', 'Light'), value: 'light', name: 'light' },
    { label: t('menu.theme.dark', 'Dark'), value: 'dark', name: 'dark' },
    { label: t('menu.theme.system', 'System'), value: 'system', name: 'system' },
  ]
  const [themeOpen, setThemeOpen] = React.useState(false)
  const themePreference = JSON.parse(localStorage.getItem('fs.discovery.theme'))
  const selectedOption = chips.find((chip) => chip.value === themePreference) ?? chips[2]
  selectedOption.selected = true

  return (
    <ExpandableListItem open={themeOpen} setOpen={setThemeOpen} primaryText={t('menu.theme', 'Theme')}>
      <fieldset>
        <legend style={{ fontSize: '.8em' }}>{t('menu.theme.options', 'Theme Options')}</legend>
        <Separator size="xs" />
        <ChoiceChips
          name="themeSelection"
          chips={chips}
          onSelectionChange={(chip) => {
            handlePreferenceToggle(chip?.value)
          }}
        />
      </fieldset>
    </ExpandableListItem>
  )
}

const More = () => {
  const { t } = useTranslation()
  const overlay = useOverlay()
  const initData = { page_detail: 'More' }
  usePageView(initData)
  useExternalLinks()
  const returnUrl = `${encodeURIComponent(`${appPath}`)}`
  const logoutUrl = `/auth/logout?returnUrl=${returnUrl}`
  const isPWAMode = usePWAMode()
  const isTWAMode = getIsTWAMode()
  const showPWAorTWAMenuItems = isPWAMode || isTWAMode

  const { open } = useUsabilla({
    url: '//w.usabilla.com/e646f09e862d.js',
  })
  const showStatusOverlay = useStatusOverlay()

  const sendUsabillaFeedback = async () => {
    const wasAbleToOpen = await open()
    if (!wasAbleToOpen) {
      showStatusOverlay({
        message: t('error.usabilla', 'Please turn off any addblockers to use our feedback system.'),
        type: 'error',
      })
    }
  }
  const langText = findLang(i18n.language, t)
  return (
    <LayoutBand maxWidth="xxs" marginY="sm">
      <List>
        <H1 size="H3" centered>
          {t('nav.more', 'More')}
        </H1>
        <MoreMenuItem
          primaryText={t('nav.manage-groups', 'Manage Groups')}
          to="/groups"
          linkName="More.MANAGE_GROUPS:CLICK"
        />
        <MoreMenuItem
          primaryText={t('nav.notifications', 'Notifications')}
          to="/notifications"
          linkName="More.NOTIFICATIONS:CLICK"
        />
        <MoreMenuItem
          primaryText={t('nav.settings', 'Settings')}
          to="/identity/settings/profile/"
          linkName="More.PROFILE_SETTINGS:CLICK"
          external
          target="_blank"
        />
        <MoreMenuItem
          primaryText={t('nav.familysearch-apps', 'FamilySearch Apps')}
          to="/mobile-apps/"
          linkName="More.MOBILE_APPS:CLICK"
          external
          target="_blank"
        />
        <ThemeMenuItem />
        {showPWAorTWAMenuItems && (
          <MoreMenuItem
            primaryText={t('more.child-safety-standards', 'Child Safety Standards')}
            to="/child-safety-standards"
          />
        )}
        <ListItem
          primaryText={t('nav.submit-feedback', 'Submit Feedback')}
          onClick={() => {
            trackEvent({ event_name: 'More.SUBMIT_FEEDBACK:CLICK' })
            sendUsabillaFeedback()
          }}
        />
        {showPWAorTWAMenuItems && (
          <MoreMenuItem
            icon={PlaceWorld}
            primaryText={langText}
            onClick={() => {
              trackEvent({ event_name: 'More.LANGUAGE_PICKER:CLICK' })
              overlay.handleClick()
            }}
          />
        )}

        <MoreMenuItem
          primaryText={t('more.signout', 'Sign out')}
          to={logoutUrl}
          external
          target="_self"
          linkName="More.SIGN_OUT:CLICK"
          onClick={clearDiscoveryCodeNotification}
        />
      </List>

      <LangPicker overlay={overlay} />
    </LayoutBand>
  )
}

export default More
